import type { AlarmTopic } from '@/features/alarmTopics/types'
import type { FleetSeverity, SiteSeverityRule } from './types'

/** Rule state of one severity across the selected sites. */
export type RuleCoverage = 'all' | 'some' | 'none'

export interface SeverityRuleState {
  /** Selected sites that have the rule ON. */
  onCount: number
  coverage: RuleCoverage
}

const ruleKey = (controllerId: string, severityId: number) => `${controllerId}\u0000${severityId}`

/** ON rules as a lookup set; explicit OFF rules count as "no subscription". */
export function rulesOnSet(rules: SiteSeverityRule[]): Set<string> {
  return new Set(rules.filter((r) => r.is_subscribed).map((r) => ruleKey(r.controller_id, r.alarm_severity_id)))
}

export function ruleState(onRules: Set<string>, severityId: number, selectedIds: string[]): SeverityRuleState {
  const onCount = selectedIds.filter((id) => onRules.has(ruleKey(id, severityId))).length
  const coverage: RuleCoverage =
    selectedIds.length > 0 && onCount === selectedIds.length ? 'all' : onCount > 0 ? 'some' : 'none'
  return { onCount, coverage }
}

/** What a card shows as chosen: the user's pending choice, else the saved state ("some" counts as not chosen). */
export function isChosen(draft: Record<number, boolean>, severityId: number, state: SeverityRuleState): boolean {
  return draft[severityId] ?? state.coverage === 'all'
}

/**
 * Save payload for the pending choices: chosen -> true (subscribe every
 * selected site), unchosen -> null (remove the rule at every selected
 * site). Choices that match the saved state are left out, so an untouched
 * "some sites" severity is never changed by saving another one.
 */
export function savePayload(
  draft: Record<number, boolean>,
  onRules: Set<string>,
  selectedIds: string[],
): Record<number, boolean | null> {
  const payload: Record<number, boolean | null> = {}
  for (const [key, chosen] of Object.entries(draft)) {
    const severityId = Number(key)
    const { coverage } = ruleState(onRules, severityId, selectedIds)
    if (chosen && coverage !== 'all') payload[severityId] = true
    if (!chosen && coverage !== 'none') payload[severityId] = null
  }
  return payload
}

export interface TopicGroup {
  /** null for alarm types with no (or an unknown) severity. */
  severityId: number | null
  name: string
  topics: AlarmTopic[]
}

/**
 * Alarm types grouped by the severities the overview reports, in its
 * most-severe-first order. Topics with no or an unrecognised severity are
 * kept in a separate "Uncategorised" group: they can be set individually
 * but are never covered by a severity subscription or default.
 */
export function groupTopicsBySeverity(severities: FleetSeverity[], topics: AlarmTopic[]): TopicGroup[] {
  const groups: TopicGroup[] = severities.map((s) => ({
    severityId: s.alarm_severity_id,
    name: s.alarm_severity_name,
    topics: [],
  }))
  const byId = new Map(groups.map((g) => [g.severityId, g]))
  const uncategorised: TopicGroup = { severityId: null, name: 'Uncategorised', topics: [] }
  for (const topic of topics) {
    const group = topic.alarm_severity_id == null ? undefined : byId.get(topic.alarm_severity_id)
    ;(group ?? uncategorised).topics.push(topic)
  }
  for (const group of [...groups, uncategorised]) {
    group.topics.sort((a, b) => a.alarm_real_name.localeCompare(b.alarm_real_name))
  }
  return uncategorised.topics.length ? [...groups, uncategorised] : groups
}

/** "Site A", "Site A and Site B", "Site A, Site B and 3 more". */
export function siteListLabel(names: string[], max = 2): string {
  if (names.length === 0) return 'no sites'
  if (names.length === 1) return names[0]
  if (names.length <= max) return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
  return `${names.slice(0, max).join(', ')} and ${names.length - max} more`
}

export function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`
}
